/**
 * Cross-app marketing attribution helpers (UTM + click IDs + referrer).
 *
 * Used by:
 * - qrhub.io (this site): capture on landing, bridge via URL params on CTA
 * - dashboard.qrhub.io: capture from URL (including bridged params), attach to GTM events
 */

export const UTM_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  // Common GA4/Google Ads optional UTM keys
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
] as const;

export const CLICK_ID_KEYS = [
  // Google Ads / GA4
  "gclid",
  "gbraid",
  "wbraid",
  // Meta
  "fbclid",
  // Microsoft Ads
  "msclkid",
  // TikTok
  "ttclid",
] as const;

/** Extra keys bridged cross-domain from marketing site → dashboard. */
export const BRIDGE_PARAM_KEYS = [
  "referrer",
  "referrer_host",
  "inferred_source",
  "valid_until",
] as const;

export type AttributionKey =
  | (typeof UTM_PARAM_KEYS)[number]
  | (typeof CLICK_ID_KEYS)[number];

export type AttributionParams = Partial<Record<AttributionKey, string>>;

export interface AttributionTouch extends AttributionParams {
  capturedAt: string; // ISO timestamp
  validUntil?: string; // ISO timestamp — attribution expires after this
  landingPath?: string; // pathname + search
  referrer?: string; // full document.referrer
  referrer_host?: string; // derived hostname
  inferred_source?: string; // computed best-effort source label
}

export interface AttributionStateV1 {
  v: 1;
  first?: AttributionTouch;
  last?: AttributionTouch;
}

export const ATTRIBUTION_STORAGE_KEY = "qrhub_attribution_v1";
export const ATTRIBUTION_COOKIE_KEY = "qrhub_attribution_v1";

/** Default TTL for dashboard attribution (90 days). Not used on the marketing site, kept for parity. */
export const ATTRIBUTION_MAX_AGE_SECONDS_DASHBOARD = 60 * 60 * 24 * 90;

/** TTL for marketing-site attribution (7 days). This is what qrhub.io should use. */
export const ATTRIBUTION_MAX_AGE_SECONDS_MARKETING = 60 * 60 * 24 * 7;

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function safeJsonParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  try {
    const found = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    if (!found) return null;
    return found.substring(name.length + 1);
  } catch {
    return null;
  }
}

function setCookie(
  name: string,
  value: string,
  options: {
    maxAgeSeconds: number;
    path?: string;
    sameSite?: "Lax" | "Strict";
  } = {
    maxAgeSeconds: ATTRIBUTION_MAX_AGE_SECONDS_DASHBOARD,
    path: "/",
    sameSite: "Lax",
  },
): void {
  if (!isBrowser()) return;
  const path = options.path ?? "/";
  const sameSite = options.sameSite ?? "Lax";
  const maxAgeSeconds = options.maxAgeSeconds;
  document.cookie = `${name}=${value}; path=${path}; max-age=${maxAgeSeconds}; SameSite=${sameSite}`;
}

function normalizeState(raw: unknown): AttributionStateV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const v = (raw as { v?: unknown }).v;
  if (v !== 1) return null;
  return raw as AttributionStateV1;
}

function isTouchExpired(touch: AttributionTouch | undefined): boolean {
  if (!touch?.validUntil) return false;
  const until = Date.parse(touch.validUntil);
  if (Number.isNaN(until)) return false;
  return Date.now() > until;
}

export function getAttributionState(): AttributionStateV1 | null {
  if (!isBrowser()) return null;

  const fromLocal = safeJsonParse<unknown>(
    window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY),
  );
  const normalizedLocal = normalizeState(fromLocal);
  if (normalizedLocal) {
    const touch = normalizedLocal.last ?? normalizedLocal.first;
    if (isTouchExpired(touch)) return null;
    return normalizedLocal;
  }

  const fromCookieRaw = readCookie(ATTRIBUTION_COOKIE_KEY);
  if (!fromCookieRaw) return null;
  const decoded = safeJsonParse<unknown>(decodeURIComponent(fromCookieRaw));
  const normalizedCookie = normalizeState(decoded);
  if (!normalizedCookie) return null;
  const touch = normalizedCookie.last ?? normalizedCookie.first;
  if (isTouchExpired(touch)) return null;
  return normalizedCookie;
}

export function setAttributionState(
  state: AttributionStateV1,
  options?: { maxAgeSeconds?: number },
): void {
  if (!isBrowser()) return;

  const maxAgeSeconds =
    options?.maxAgeSeconds ?? ATTRIBUTION_MAX_AGE_SECONDS_DASHBOARD;

  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage quota / blocked storage scenarios
  }

  try {
    setCookie(ATTRIBUTION_COOKIE_KEY, encodeURIComponent(JSON.stringify(state)), {
      maxAgeSeconds,
      path: "/",
      sameSite: "Lax",
    });
  } catch {
    // Ignore cookie write failures
  }
}

export function extractAttributionParamsFromSearch(
  search: string,
): AttributionParams {
  const params = new URLSearchParams(
    search.startsWith("?") ? search : `?${search}`,
  );
  const out: AttributionParams = {};

  for (const key of [...UTM_PARAM_KEYS, ...CLICK_ID_KEYS]) {
    const value = params.get(key);
    if (value && value.trim()) {
      out[key] = value.trim();
    }
  }

  return out;
}

export function hasAttributionParams(search: string): boolean {
  const extracted = extractAttributionParamsFromSearch(search);
  return Object.keys(extracted).length > 0;
}

function tryGetReferrer(): string {
  if (!isBrowser()) return "";
  try {
    return typeof document.referrer === "string" ? document.referrer : "";
  } catch {
    return "";
  }
}

function tryGetReferrerHost(referrer: string): string {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname;
  } catch {
    return "";
  }
}

function inferSourceLabelFromHost(host: string): string {
  const h = host.toLowerCase();

  // Social / messaging
  if (
    h === "facebook.com" ||
    h.endsWith(".facebook.com") ||
    h === "l.facebook.com" ||
    h === "m.facebook.com"
  )
    return "facebook";
  if (h === "instagram.com" || h.endsWith(".instagram.com")) return "instagram";
  if (h === "whatsapp.com" || h.endsWith(".whatsapp.com")) return "whatsapp";
  if (
    h === "t.co" ||
    h === "twitter.com" ||
    h.endsWith(".twitter.com") ||
    h === "x.com" ||
    h.endsWith(".x.com")
  )
    return "twitter";
  if (h === "linkedin.com" || h.endsWith(".linkedin.com")) return "linkedin";
  if (h === "tiktok.com" || h.endsWith(".tiktok.com")) return "tiktok";

  // Search / portals (best effort)
  if (h === "google.com" || h.endsWith(".google.com")) return "google";
  if (h === "bing.com" || h.endsWith(".bing.com")) return "bing";
  if (h === "yahoo.com" || h.endsWith(".yahoo.com")) return "yahoo";

  return host; // fallback: raw host
}

function inferSource(options: {
  extracted: AttributionParams;
  referrerHost: string;
  bridgedInferredSource?: string;
}): string {
  const { extracted, referrerHost, bridgedInferredSource } = options;

  // 1) utm_source
  const utmSource = extracted.utm_source;
  if (typeof utmSource === "string" && utmSource.trim()) {
    return utmSource.trim();
  }

  // 2) click IDs
  if (extracted.gclid || extracted.gbraid || extracted.wbraid) return "google";
  if (extracted.fbclid) return "facebook";
  if (extracted.msclkid) return "microsoft";
  if (extracted.ttclid) return "tiktok";

  // 3) bridged inferred_source (not applicable on first-touch capture, kept for parity)
  if (bridgedInferredSource && bridgedInferredSource.trim()) {
    return bridgedInferredSource.trim();
  }

  // 4) referrer host
  if (referrerHost) return inferSourceLabelFromHost(referrerHost);

  // 5) direct
  return "(direct)";
}

/**
 * Captures attribution parameters (UTM + common click IDs) from the current URL,
 * and persists them for later steps (first-touch preserved, last-touch updated).
 *
 * @param options.maxAgeSeconds - Cookie max-age (pass ATTRIBUTION_MAX_AGE_SECONDS_MARKETING = 7 days).
 * @param options.ttlSeconds - Writes `validUntil = now + ttlSeconds` on the touch (also 7 days here).
 */
export function captureAttributionFromCurrentUrl(options?: {
  storeLandingPath?: boolean;
  maxAgeSeconds?: number;
  ttlSeconds?: number;
}): { captured: boolean; state: AttributionStateV1 } {
  const storeLandingPath = options?.storeLandingPath ?? true;
  const maxAgeSeconds =
    options?.maxAgeSeconds ?? ATTRIBUTION_MAX_AGE_SECONDS_DASHBOARD;

  if (!isBrowser()) {
    return { captured: false, state: { v: 1 } };
  }

  const extracted = extractAttributionParamsFromSearch(window.location.search);
  const hasAny = Object.keys(extracted).length > 0;

  const referrer = tryGetReferrer();
  const referrerHost = tryGetReferrerHost(referrer);
  const inferredSource = inferSource({ extracted, referrerHost });

  const existing = getAttributionState() ?? { v: 1 };

  const hasAnyContext = Boolean(referrer) || Boolean(referrerHost);
  const shouldPersist = hasAny || hasAnyContext || inferredSource !== "(direct)";
  if (!shouldPersist) return { captured: false, state: existing };

  const capturedAt = new Date().toISOString();
  let validUntil: string | undefined;
  if (options?.ttlSeconds) {
    validUntil = new Date(Date.now() + options.ttlSeconds * 1000).toISOString();
  }

  const touch: AttributionTouch = {
    ...extracted,
    capturedAt,
    ...(validUntil ? { validUntil } : {}),
    ...(referrer ? { referrer } : {}),
    ...(referrerHost ? { referrer_host: referrerHost } : {}),
    inferred_source: inferredSource,
    ...(storeLandingPath
      ? { landingPath: `${window.location.pathname}${window.location.search}` }
      : {}),
  };

  const next: AttributionStateV1 = {
    v: 1,
    first: existing.first ?? touch,
    last: touch,
  };

  setAttributionState(next, { maxAgeSeconds });
  return { captured: true, state: next };
}

/**
 * Builds query params to append when bridging attribution from marketing → dashboard.
 * Returns an empty object if there is no valid (non-expired) attribution stored.
 */
export function getAttributionBridgeParams(): Record<string, string> {
  const state = getAttributionState();
  const touch = state?.last ?? state?.first;
  if (!touch || isTouchExpired(touch)) return {};

  const out: Record<string, string> = {};

  for (const key of [...UTM_PARAM_KEYS, ...CLICK_ID_KEYS]) {
    const value = touch[key];
    if (typeof value === "string" && value.trim()) {
      out[key] = value.trim();
    }
  }

  if (touch.referrer?.trim()) out.referrer = touch.referrer.trim();
  if (touch.referrer_host?.trim()) out.referrer_host = touch.referrer_host.trim();
  if (touch.inferred_source?.trim())
    out.inferred_source = touch.inferred_source.trim();
  if (touch.validUntil?.trim()) out.valid_until = touch.validUntil.trim();

  return out;
}
